import * as React from 'react';
import { useMemo } from 'react';

/** Material UI Packages. */
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Dialog,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/** Internal Components. */
import { PaperComponent } from 'shared/ui/DraggableDialog';
import DialogHeader from 'shared/ui/DialogHeader';
import { CustomSkeleton2 } from 'shared/ui/Skeleton';
import { formatOperationDateTime } from 'shared/lib/dateFormatter';

import useFetchHistory from '../../hooks/useFetchHistory';

/** Master (main-row) tables whose il_history entries represent edits to the shipment's own attributes. */
const MASTER_TABLENAMES = ['il_ocean_import', 'il_ocean_export', 'il_air_import', 'il_air_export', 'il_ftl_ltl'];

/** Attributes that are required on every shipment type, flagged in the history dialog. */
const REQUIRED_ATTRIBUTES = ['il_trade_partner_id_client', 'il_team_id'];

/** Attributes whose name has no `_id_` marker to strip, so the generic rule would show the raw column. */
const ATTRIBUTE_LABELS = { il_team_id: 'TEAM' };

/** Formats a raw attribute name into a user-friendly label, e.g. "il_location_id_aod" -> "AOD". */
function formatAttributeLabel(attribute) {
    if (ATTRIBUTE_LABELS[attribute]) return ATTRIBUTE_LABELS[attribute];
    const idMarker = '_id_';
    const idx = attribute.lastIndexOf(idMarker);
    const relevant = idx !== -1 ? attribute.slice(idx + idMarker.length) : attribute;
    return relevant.replace(/_/g, ' ').toUpperCase();
}

export default function MainHistoryDialog({ open, onClose, row, tabValue }) {
    /** Only fetch/read il_history while the dialog is actually open, so Copy/Note clicks don't trigger it. */
    const historyRowId = open ? row?.id : null;
    const { historyList, isReady } = useFetchHistory(tabValue, historyRowId);

    /** Group this row's own-attribute history entries by attribute, sorted chronologically within each group. */
    const groupedHistory = useMemo(() => {
        if (!historyRowId) return [];
        const matched = (historyList[tabValue] || []).filter(
            (item) => item.table_id === historyRowId && MASTER_TABLENAMES.includes(item.tablename)
        );

        const groups = matched.reduce((acc, item) => {
            if (!acc[item.attribute]) acc[item.attribute] = [];
            acc[item.attribute].push(item);
            return acc;
        }, {});

        return Object.entries(groups)
            .map(([attribute, entries]) => ({
                attribute,
                entries: [...entries].sort((a, b) => new Date(a.modified_on) - new Date(b.modified_on)),
            }))
            .sort((a, b) => a.attribute.localeCompare(b.attribute));
    }, [historyList, tabValue, historyRowId]);

    return (
        <Dialog
            maxWidth="sm"
            fullWidth
            onClose={onClose}
            open={open}
            PaperComponent={PaperComponent}
            PaperProps={{
                sx: {
                    borderRadius: '18px',
                },
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: 800 }}>
                <DialogHeader cursor title={`HISTORY | ${row?.il_opus_reference_id ?? ''}`} onClose={onClose} />
                <Box
                    sx={{
                        height: '100%',
                        p: 1,
                        bgcolor: '#dfdfdf',
                        maxHeight: '30rem',
                        overflowY: 'auto',
                    }}
                >
                    {!isReady ? (
                        <CustomSkeleton2 />
                    ) : groupedHistory.length === 0 ? (
                        <Typography sx={{ fontSize: 12, color: '#262626', textAlign: 'center', p: 2 }}>
                            No history available.
                        </Typography>
                    ) : (
                        groupedHistory.map(({ attribute, entries }) => (
                            <Accordion
                                key={attribute}
                                sx={{
                                    backgroundColor: 'transparent',
                                    border: '1px solid #dfdfdf',
                                    boxShadow: 'none',
                                    mb: 1,
                                    '&:before': { display: 'none' },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        minHeight: 30,
                                        '& .MuiAccordionSummary-content.Mui-expanded': { margin: 0 },
                                        '& .MuiAccordionSummary-content': { margin: 0 },
                                        '&.Mui-expanded': { minHeight: 30 },
                                        backgroundColor: '#fff',
                                        borderBottom: '1px solid #dfdfdf',
                                    }}
                                >
                                    <Typography sx={{ fontSize: 11, color: '#262626', fontWeight: 500 }}>
                                        {formatAttributeLabel(attribute)}
                                        {REQUIRED_ATTRIBUTES.includes(attribute) && (
                                            <Typography component="span" sx={{ fontSize: 11, color: 'red', ml: 0.25 }}>
                                                *
                                            </Typography>
                                        )}
                                    </Typography>
                                    <Typography sx={{ fontSize: 10, fontWeight: 500, color: '#262626', ml: 0.5 }}>
                                        ({entries.length})
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ padding: 0, backgroundColor: 'white' }}>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontSize: 11, fontWeight: 500 }}>
                                                        Modified On
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: 11, fontWeight: 500 }}>Value</TableCell>
                                                    <TableCell sx={{ fontSize: 11, fontWeight: 500 }}>
                                                        Modified By
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {entries.map((entry) => (
                                                    <TableRow key={entry.id}>
                                                        <TableCell sx={{ fontSize: 11 }}>
                                                            {formatOperationDateTime(entry.modified_on)}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 11 }}>{entry.value}</TableCell>
                                                        <TableCell sx={{ fontSize: 11 }}>
                                                            {entry.registered_by}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </AccordionDetails>
                            </Accordion>
                        ))
                    )}
                </Box>
            </Box>
        </Dialog>
    );
}
